import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Lock, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OWNER_EMAIL = "linda8mak@gmail.com";

/**
 * OwnerPassportGate — bank-grade extra lock for the Platform Owner (Linda).
 *
 * Behaviour
 * ---------
 * - Renders NOTHING for anyone except Linda (email match).
 * - On mount + focus, polls `/auth/owner/status`. If `requires_passport`,
 *   pops a modal that blocks the entire viewport until Linda enters her
 *   Passport ID (12-hour unlock window).
 * - Every failed attempt is throttled server-side (5 / hour) — after that
 *   Linda gets a "wait an hour" toast, never a retry loop.
 * - On success, calls `onUnlocked()` so the parent app re-fetches auth
 *   state and unhides admin routes.
 */
const OwnerPassportGate = ({ onUnlocked }) => {
  const { user, token } = useAuth();
  const [needs, setNeeds] = useState(false);
  const [passport, setPassport] = useState("");
  const [busy, setBusy] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);

  const isOwner = (user?.email || "").toLowerCase() === OWNER_EMAIL;

  const checkStatus = useCallback(async () => {
    if (!token || !isOwner) return;
    try {
      const res = await axios.get(`${API}/auth/owner/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNeeds(!!res.data?.requires_passport);
    } catch {
      // If it fails, keep the current state — non-fatal.
    }
  }, [token, isOwner]);

  useEffect(() => {
    checkStatus();
    const onFocus = () => checkStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [checkStatus]);

  const submit = async () => {
    if (!passport.trim()) {
      toast.error("Enter your Passport ID number");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/auth/owner/passport-unlock`,
        { passport_id: passport.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.ok) {
        toast.success("Passport verified · Admin Dashboard unlocked");
        setNeeds(false);
        setPassport("");
        setExpiresAt(res.data.expires_at);
        onUnlocked?.(res.data);
      }
    } catch (e) {
      const raw = e.response?.data?.detail;
      const msg = raw && typeof raw === "object" ? raw.message : raw || "Unlock failed";
      if (e.response?.status === 429) {
        toast.error("Too many failed attempts — wait one hour and try again.");
      } else {
        toast.error(msg);
      }
      setPassport("");
    } finally {
      setBusy(false);
    }
  };

  if (!isOwner || !needs) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center px-4"
      data-testid="owner-passport-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-md w-full bg-[#0A0E18] border border-[#EC008C]/50 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
        {/* Silver bank-vault ribbon — signals premium/hardware-grade security. */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 h-1 sf-silver-plate sf-silver-shine-animate"
        />
        <div className="flex items-center gap-3 mb-3">
          <div className="sf-silver-chip">
            <Shield className="w-3 h-3" />
            Bank-Grade
          </div>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <Shield className="w-6 h-6 text-[#EC008C]" />
          <h2 className="font-['Unbounded'] text-xl font-bold text-white">
            Verification Required
          </h2>
        </div>
        <p className="text-[#8A92A0] text-sm mb-6 leading-relaxed">
          Enter your Passport ID Number to unlock the Admin Dashboard and Treasury.
          Unlock expires 12 hours after verification.
        </p>

        <Label htmlFor="passport-id" className="text-white text-xs uppercase tracking-widest">
          Passport ID Number
        </Label>
        <Input
          id="passport-id"
          type="password"
          autoFocus
          value={passport}
          onChange={(e) => setPassport(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !busy) submit();
          }}
          placeholder="e.g. AB1234567"
          autoComplete="off"
          className="mt-2 bg-[#050810] border-[#1A2030] text-white font-mono h-12 text-base"
          data-testid="owner-passport-input"
        />

        <div className="mt-3 rounded-lg border border-[#1A2030] bg-[#050810] p-3 text-xs text-[#8A92A0] flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
          <span>
            5 attempts per hour. On the 6th, treasury access is locked for the day
            and every failed attempt is written to the security ledger.
          </span>
        </div>

        <Button
          onClick={submit}
          disabled={busy || !passport.trim()}
          className="w-full mt-5 bg-[#EC008C] text-black font-semibold h-12 hover:bg-[#FF3399]"
          data-testid="owner-passport-submit"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Verifying…
            </>
          ) : (
            <>
              <Lock className="w-4 h-4 mr-2" />
              Unlock Admin Dashboard
            </>
          )}
        </Button>

        {expiresAt && (
          <p className="text-[10px] text-[#8A92A0] text-center mt-3">
            Session unlocked until {new Date(expiresAt).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default OwnerPassportGate;
